import React, { useCallback, useEffect, useRef, useState } from "react";
import DesignInput from "./components/DesignInput";
import ProgressTracker from "./components/ProgressTracker";
import DownloadPanel from "./components/DownloadPanel";
import ErrorDisplay from "./components/ErrorDisplay";
import ViolationsPanel from "./components/ViolationsPanel";
import ProjectDashboard from "./components/ProjectDashboard";
import AuthForm from "./components/AuthForm";
import AdminPanel from "./components/Admin";
import { useSessionManager } from "./hooks/useSessionManager";
import type {
  AgentNode,
  ProgressMessage,
  ErrorMessage,
  Violation,
  ProjectSummary,
  ProjectCreatedData,
  WsMessage,
} from "./types";
import "./App.css";

/** WebSocket URL — sourced from env with a local-dev fallback. */
const WS_URL =
  process.env.REACT_APP_WS_URL ?? "ws://localhost:8080";

/** Generate a simple unique session ID. */
function generateSessionId(): string {
  return `session-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Main application component.
 *
 * Integrates DesignInput, ProgressTracker, DownloadPanel, ErrorDisplay,
 * and the WebSocket SessionManager for real-time communication with the
 * API Gateway backend.
 *
 * Validates: Requirements 12.2, 12.5
 */
const App: React.FC = () => {
  const [currentNode, setCurrentNode] = useState<AgentNode | null>(null);
  const [progressMessages, setProgressMessages] = useState<ProgressMessage[]>([]);
  const [errors, setErrors] = useState<ErrorMessage[]>([]);
  const [archiveUrl, setArchiveUrl] = useState<string | null>(null);
  const [schematicUrl, setSchematicUrl] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [isPCBRunning, setIsPCBRunning] = useState(false);
  const [pcbArchiveUrl, setPcbArchiveUrl] = useState<string | null>(null);
  const [violations, setViolations] = useState<Violation[]>([]);
  const [fixingIds, setFixingIds] = useState<Set<string>>(new Set());
  const [fixedIds, setFixedIds] = useState<Set<string>>(new Set());
  const [userEmail, setUserEmail] = useState<string>(localStorage.getItem("hw_user_email") || "");
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(localStorage.getItem("hw_auth_verified") === "true");
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [currentProject, setCurrentProject] = useState<ProjectCreatedData | null>(() => {
    try { const s = localStorage.getItem("hw_current_project"); return s ? JSON.parse(s) : null; } catch { return null; }
  });
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [appMode, setAppMode] = useState<"single" | "project" | "admin">(() =>
    localStorage.getItem("hw_current_project") ? "project" : "single"
  );
  const [componentStatus, setComponentStatus] = useState<any[]>([]);
  const [usageInfo, setUsageInfo] = useState<any>(null);
  const [referenceStatus, setReferenceStatus] = useState<string>("");
  const [enrichedPreview, setEnrichedPreview] = useState<string>("");
  const [referenceProjectId, setReferenceProjectId] = useState<string>("");
  const [symbolInventory, setSymbolInventory] = useState<any>(null);

  // Persist currentProject to localStorage so it survives page refresh
  useEffect(() => {
    if (currentProject) {
      localStorage.setItem("hw_current_project", JSON.stringify(currentProject));
    } else {
      localStorage.removeItem("hw_current_project");
    }
  }, [currentProject]);
  const [datasheetUploads, setDatasheetUploads] = useState<Record<string, any>>({});
  const [pendingUploads, setPendingUploads] = useState<Record<string, File>>({});
  const [forcedParts, setForcedParts] = useState<Set<string>>(new Set());
  const pendingUploadsRef = useRef<Record<string, File>>({});

  /**
   * Handle incoming WebSocket messages.
   */
  const handleWsMessage = useCallback((msg: WsMessage) => {
    switch (msg.type) {
      case "progress":
        setCurrentNode(msg.node as AgentNode);
        setProgressMessages((prev) => [...prev, msg as ProgressMessage]);
        // Check for schematic URL in progress data
        if ((msg.data as any)?.schematic_url) {
          setSchematicUrl((msg.data as any).schematic_url);
        }
        // Check for SVG preview URL
        if ((msg.data as any)?.svg_url) {
          setSchematicUrl((msg.data as any).svg_url);
        }
        // Check for build completion in progress messages (fallback for missing completion message)
        {
          const lower = msg.message.toLowerCase();
          if (lower.includes("build complete") || lower.includes("archive ready")) {
            setIsRunning(false);
            setIsComplete(true);
          }
        }
        break;
      case "completion":
        setCurrentNode("export");
        setArchiveUrl(
          (msg.data as { archive_url?: string }).archive_url ?? null
        );
        setIsRunning(false);
        setIsComplete(true);
        break;
      case "pcb_complete": {
        const pcbData = msg.data as { zip_url?: string | null; gate_passed?: boolean; drc_errors?: string[]; error?: string; error_type?: string; cleanup_checklist?: any[] };
        setPcbArchiveUrl(pcbData.zip_url ?? null);
        setIsPCBRunning(false);
        stopPcbPoll();
        if (pcbData.error) {
          setErrors((prev) => [...prev, { type: "error", session_id: "", node: "pcb", message: `PCB build failed: ${pcbData.error}`, timestamp: "", data: { error_type: pcbData.error_type || "pcb_error", retry_count: 0, details: null } } as ErrorMessage]);
        }
        if (pcbData.cleanup_checklist && pcbData.cleanup_checklist.length > 0) {
          // Store checklist HTML for user-triggered popup
          // Note: layer count and specs are provided by the backend via pcbData
          const layerCount = (pcbData as any).layer_count || 4;
          const htmlContent = `<!DOCTYPE html><html><head><title>PCB Cleanup Checklist</title><style>body{font-family:monospace;padding:20px;max-width:800px;margin:0 auto}h1{color:#333;border-bottom:2px solid #333}h2{color:#555;margin-top:20px}.item{padding:8px 12px;margin:4px 0;border-left:4px solid #e74c3c;background:#fef9f9}.item.p1{border-color:#f39c12;background:#fef9e7}.item.ok{border-color:#27ae60;background:#eafaf1}.severity{font-weight:bold;margin-right:8px}.meta{color:#777;font-size:0.85em;margin-top:15px}@media print{body{padding:0}.no-print{display:none}}</style></head><body><h1>\ud83d\udccb PCB Manual Cleanup Checklist</h1><h2>Project: ${msg.session_id}</h2><p>Generated: ${new Date().toLocaleString()}</p><p><strong>Manufacturing: 0.15mm clearance, 0.30mm via drill, ${layerCount}-layer stackup</strong></p><hr/>${pcbData.cleanup_checklist.map((item: any) => `<div class="item ${item.severity.toLowerCase()}"><span class="severity">[${item.severity}]</span>${item.action}</div>`).join('')}<hr/><div class="meta"><p>After manual fixes: re-run DRC in KiCad \u2192 confirm all zeros \u2192 export final Gerbers</p></div><div class="no-print" style="margin-top:20px"><button onclick="window.print()" style="padding:10px 20px;font-size:16px;cursor:pointer;background:#2196F3;color:white;border:none;border-radius:4px">\ud83d\udda8\ufe0f Print / Save as PDF</button></div></body></html>`;
          (window as any).__pcbChecklistHtml = htmlContent;
          const checklistMsgs = pcbData.cleanup_checklist.map((item: any) => ({
            type: "progress" as const,
            session_id: msg.session_id,
            node: "pcb" as AgentNode,
            message: `[${item.severity}] ${item.action}`,
            timestamp: "",
          }));
          setProgressMessages((prev) => [...prev,
            { type: "progress", session_id: msg.session_id, node: "pcb" as AgentNode, message: "── Manual Cleanup Checklist ──", timestamp: "" } as ProgressMessage,
            ...checklistMsgs as ProgressMessage[],
            { type: "progress", session_id: msg.session_id, node: "pcb" as AgentNode, message: "__CHECKLIST_BUTTONS__", timestamp: "" } as ProgressMessage,
          ]);
        }
        break;
      }
      case "violations": {
        const newViolations = (msg.data as { violations: Violation[] }).violations || [];
        setViolations((prev) => {
          const existingIds = new Set(prev.map((v) => v.id));
          const unique = newViolations.filter((v: Violation) => !existingIds.has(v.id));
          return [...prev, ...unique];
        });
        break;
      }
      case "fix_result": {
        const { violation_id, success } = msg.data as { violation_id: string; success: boolean };
        setFixingIds((prev) => { const n = new Set(prev); n.delete(violation_id); return n; });
        if (success) {
          setFixedIds((prev) => new Set(prev).add(violation_id));
        }
        break;
      }
      case "project_created": {
        const pd = msg.data as unknown as ProjectCreatedData;
        setCurrentProject(pd);
        setIsCreatingProject(false);
        if ((pd as any).component_status) {
          setComponentStatus((pd as any).component_status);
        }
        break;
      }
      case "component_status": {
        const cs = (msg.data as { components: any[] }).components || [];
        setComponentStatus(cs);
        break;
      }
      case "upload_url": {
        const { upload_url, s3_key, part_number } = msg.data as { upload_url: string; s3_key: string; part_number: string };
        // Upload the pending file to S3 via presigned URL
        const file = pendingUploadsRef.current[part_number];
        if (file && upload_url) {
          setDatasheetUploads((prev) => ({ ...prev, [part_number]: { ...prev[part_number], status: "uploading" } }));
          fetch(upload_url, { method: "PUT", body: file, headers: { "Content-Type": "application/pdf" } })
            .then((resp) => {
              if (resp.ok) {
                setDatasheetUploads((prev) => ({ ...prev, [part_number]: { ...prev[part_number], status: "processing" } }));
                // Tell backend to process the uploaded file
                send({ action: "upload_datasheet", project_id: msg.session_id, part_number, s3_key });
              } else {
                setDatasheetUploads((prev) => ({ ...prev, [part_number]: { ...prev[part_number], status: "error" } }));
              }
            })
            .catch(() => {
              setDatasheetUploads((prev) => ({ ...prev, [part_number]: { ...prev[part_number], status: "error" } }));
            });
        }
        break;
      }
      case "datasheet_processed": {
        const { part_number: pn, pin_count } = msg.data as { part_number: string; pin_count: number };
        setDatasheetUploads((prev) => ({ ...prev, [pn]: { ...prev[pn], status: "done", pinCount: pin_count } }));
        // Also mark JSON upload as done if this was a component_json approval
        setDatasheetUploads((prev) => {
          const jk = pn + "_json";
          if (prev[jk] && prev[jk].status === "processing") {
            return { ...prev, [jk]: { ...prev[jk], status: "done", pinCount: pin_count } };
          }
          return prev;
        });
        break;
      }
      case "component_json_ready": {
        const { download_url, part_number: pnj, source } = msg.data as { download_url: string; part_number: string; source?: string };
        if (download_url) {
          // Show warning for draft JSONs
          if (source === "draft") {
            const proceed = window.confirm(
              `⚠️ ${pnj}.circuit.json is an AI-generated DRAFT only.\n\n` +
              `Review and upload it back for approval before it can be used in builds.\n\n` +
              `Download now?`
            );
            if (!proceed) break;
          }
          const dl = document.createElement("a"); dl.href = download_url; dl.target = "_blank"; dl.rel = "noopener"; document.body.appendChild(dl); dl.click(); document.body.removeChild(dl);
        }
        break;
      }
      case "upload_rejected": {
        const { part_number: prn, errors: rejErrors } = msg.data as { part_number: string; errors: string[] };
        const rKey = prn + "_json";
        setDatasheetUploads((prev) => ({
          ...prev,
          [rKey]: { ...prev[rKey], status: "rejected", errors: rejErrors },
        }));
        break;
      }
      case "project_list": {
        const pl = (msg.data as { projects: ProjectSummary[] }).projects || [];
        setProjects(pl);
        break;
      }
      case "project_detail": {
        const proj = (msg.data as { project: any }).project;
        if (proj) {
          setCurrentProject({
            project_id: proj.project_id,
            project_name: proj.project_name,
            description: proj.description || "",
            sheet_count: proj.sheet_count || 0,
            sheets: (proj.sheet_names || []).map((n: string) => ({ name: n, description: "" })),
            urls: proj.urls || {},
          });
        }
        break;
      }
      case "error":
        setErrors((prev) => [...prev, msg as ErrorMessage]);
        setIsRunning(false);
        setIsCreatingProject(false);
        break;
      case "admin_stats":
      case "usage_info":
        // Dispatch to AdminPanel via window event
        window.dispatchEvent(new CustomEvent(msg.type, { detail: msg }));
        if (msg.type === "usage_info" && (msg.data as any)?.projects) {
          setUsageInfo(msg.data);
        }
        break;
      case "reference_upload_url": {
        const { upload_url: refUrl, s3_key: refKey } = msg.data as { upload_url: string; s3_key: string };
        const refFile = (window as any).__pendingReferenceFile;
        if (refFile && refUrl) {
          setReferenceStatus("uploading");
          fetch(refUrl, { method: "PUT", body: refFile, headers: { "Content-Type": "application/zip" } })
            .then((resp) => {
              if (resp.ok) {
                setReferenceStatus("processing");
                send({ action: "upload_reference", project_id: msg.session_id, s3_key: refKey, nl_description: (window as any).__pendingReferencePrompt || "" });
              } else {
                setReferenceStatus("error");
              }
            })
            .catch(() => setReferenceStatus("error"));
        }
        break;
      }
      case "reference_processed": {
        const { enriched_prompt, summary } = msg.data as { enriched_prompt: string; summary: string };
        setReferenceStatus("done");
        setEnrichedPreview(enriched_prompt || "");
        setProgressMessages((prev) => [...prev, {
          type: "progress", session_id: msg.session_id, node: "planner" as AgentNode,
          message: `Reference analysis complete: ${summary}`, timestamp: "",
        } as ProgressMessage]);
        break;
      }
      case "symbol_inventory": {
        setSymbolInventory(msg.data);
        break;
      }
      case "symbol_approved":
      case "symbol_rejected": {
        // Refresh inventory on approval/rejection
        const { part_number: approvedPart, status: approvalStatus } = msg.data as { part_number: string; status: string };
        setSymbolInventory((prev: any) => {
          if (!prev) return prev;
          return {
            ...prev,
            components: prev.components.map((c: any) =>
              c.part_number === approvedPart ? { ...c, status: approvalStatus === "approved" ? "APPROVED" : "REJECTED" } : c
            ),
            pending_review: prev.components.some((c: any) =>
              c.part_number !== approvedPart && c.status === "GENERATED_PENDING_REVIEW"
            ),
          };
        });
        break;
      }
      case "symbol_download_url": {
        const { download_url: symUrl } = msg.data as { download_url: string };
        if (symUrl) {
          const dl = document.createElement("a");
          dl.href = symUrl; dl.target = "_blank"; dl.rel = "noopener";
          document.body.appendChild(dl); dl.click(); document.body.removeChild(dl);
        }
        break;
      }
    }
  }, []);

  // ── WebSocket session management ──────────────────────────────────
  const { connectionState, send, connect } = useSessionManager({
    url: WS_URL,
    onMessage: handleWsMessage,
  });

  /** Submit a design description over the WebSocket. */
  const handleSubmit = useCallback(
    (description: string) => {
      // Reset UI state for a new session
      setCurrentNode(null);
      setProgressMessages([]);
      setErrors([]);
      setArchiveUrl(null);
      setSchematicUrl(null);
      setViolations([]);
      setFixingIds(new Set());
      setFixedIds(new Set());
      setIsRunning(true);
      setIsComplete(false);

      const sessionId = generateSessionId();
      send({
        action: "submit",
        session_id: sessionId,
        nl_description: description,
        user_email: userEmail,
      });
    },
    [send, userEmail]
  );

  const handleFixViolation = useCallback(
    (violation: Violation) => {
      setFixingIds((prev) => new Set(prev).add(violation.id));
      send({
        action: "fix_violation",
        session_id: currentProject?.project_id || "",
        violation_id: violation.id,
        violation_source: violation.source,
        violation_message: violation.message,
        violation_severity: violation.severity,
        violation_location: violation.location || "",
      });
      // Timeout: if no response in 3 min, clear fixing state
      setTimeout(() => {
        setFixingIds((prev) => {
          if (prev.has(violation.id)) {
            const n = new Set(prev);
            n.delete(violation.id);
            return n;
          }
          return prev;
        });
      }, 180_000);
    },
    [send, currentProject]
  );

  const handleIgnoreViolation = useCallback((_id: string) => {}, []);

  const handleCreateProject = useCallback(
    (description: string, enrichWithGemini?: boolean) => {
      setIsCreatingProject(true);
      send({
        action: "create_project",
        user_email: userEmail,
        nl_description: description,
        enrich_with_gemini: enrichWithGemini || false,
        ...(referenceProjectId ? { reference_project_id: referenceProjectId } : {}),
      });
    },
    [send, userEmail, referenceProjectId]
  );

  const handleListProjects = useCallback(
    () => send({ action: "list_projects", user_email: userEmail }),
    [send, userEmail]
  );

  const handleSelectProject = useCallback(
    (projectId: string) => send({ action: "get_project", user_email: userEmail, project_id: projectId }),
    [send, userEmail]
  );

  const handleBuildProject = useCallback(
    (projectId: string) => {
      setIsRunning(true);
      send({ action: "build_project", user_email: userEmail, project_id: projectId });
    },
    [send, userEmail]
  );

  const pcbProjectIdRef = useRef<string | null>(null);
  const pcbPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPcbPoll = useCallback(() => {
    if (pcbPollRef.current) {
      clearInterval(pcbPollRef.current);
      pcbPollRef.current = null;
    }
  }, []);

  // Poll S3 for PCB ZIP every 15s as fallback when WebSocket drops
  const startPcbPoll = useCallback((projectId: string) => {
    stopPcbPoll();
    const apiBase = process.env.REACT_APP_API_URL ?? "";
    if (!apiBase) return; // No REST API configured, rely on WebSocket only
    pcbPollRef.current = setInterval(async () => {
      try {
        const resp = await fetch(`${apiBase}/pcb-status?project_id=${projectId}`, {
          headers: { "x-user-email": userEmail },
        });
        if (!resp.ok) return;
        const data = await resp.json();
        if (data.status === "complete" && data.zip_url) {
          setPcbArchiveUrl(data.zip_url);
          setIsPCBRunning(false);
          setProgressMessages((prev) => [...prev, {
            type: "progress", session_id: projectId, node: "pcb",
            message: "PCB complete (recovered via poll)", timestamp: "",
          } as ProgressMessage]);
          stopPcbPoll();
        }
      } catch {
        // poll silently
      }
    }, 15_000);
  }, [userEmail, stopPcbPoll]);

  const handleBuildPCB = useCallback(
    (projectId: string, config?: any) => {
      setIsPCBRunning(true);
      setPcbArchiveUrl(null);
      setProgressMessages([]);
      setErrors([]);
      pcbProjectIdRef.current = projectId;
      startPcbPoll(projectId);
      // Pass full PCB config including board dimensions, layers, etc.
      send({ 
        action: "build_pcb", 
        user_email: userEmail, 
        project_id: projectId, 
        pcb_profile: config?.pcb_profile || "generic_dynamic",
        pcb_config: config,
      });
    },
    [send, userEmail, startPcbPoll]
  );

  const handleConfigurePCB = useCallback(
    (projectId: string, config: any) => {
      send({ action: "configure_pcb", user_email: userEmail, project_id: projectId, pcb_config: config });
    },
    [send, userEmail]
  );

  const handleUploadSchematics = useCallback(
    (projectId: string, files: Record<string, string>) => {
      send({ action: "upload_schematic", user_email: userEmail, project_id: projectId, session_id: projectId, files });
    },
    [send, userEmail]
  );

  const handleAbortProject = useCallback(() => {
    setCurrentProject(null);
    setComponentStatus([]);
    setDatasheetUploads({});
    setIsRunning(false);
    setIsCreatingProject(false);
    setIsComplete(false);
    setProgressMessages([]);
    setViolations([]);
  }, []);

  const handleUploadDatasheet = useCallback(
    (projectId: string, partNumber: string, fileName: string) => {
      send({ action: "upload_datasheet", project_id: projectId, part_number: partNumber, file_name: fileName, request_upload: true });
    },
    [send]
  );

  const handleFileSelected = useCallback(
    (projectId: string, partNumber: string, file: File) => {
      const isJson = file.name.toLowerCase().endsWith(".json");

      if (isJson) {
        // Component JSON upload — read, validate, send via WebSocket
        const trackKey = partNumber + "_json";
        setDatasheetUploads((prev) => ({ ...prev, [trackKey]: { fileName: file.name, status: "uploading" } }));
        const reader = new FileReader();
        reader.onload = (ev) => {
          const text = ev.target?.result;
          if (typeof text === "string") {
            try {
              const parsed = JSON.parse(text);
              send({
                action: "upload_component_json",
                project_id: projectId,
                part_number: partNumber,
                component_json: parsed,
              });
              // Don't mark as done yet — wait for backend response
              setDatasheetUploads((prev) => ({
                ...prev,
                [trackKey]: { fileName: file.name, status: "processing" },
              }));
            } catch {
              setDatasheetUploads((prev) => ({ ...prev, [trackKey]: { fileName: file.name, status: "error", errors: ["Invalid JSON file"] } }));
            }
          }
        };
        reader.readAsText(file);
      } else {
        // PDF datasheet upload — existing presigned URL flow
        pendingUploadsRef.current[partNumber] = file;
        setDatasheetUploads((prev) => ({ ...prev, [partNumber]: { fileName: file.name, status: "uploading" } }));
        send({ action: "upload_datasheet", project_id: projectId, part_number: partNumber, file_name: file.name, request_upload: true });
      }
    },
    [send]
  );

  const handleAuthenticated = useCallback((email: string, _name: string) => {
    setUserEmail(email);
    setIsAuthenticated(true);
  }, []);

  // Request usage info when authenticated and connected
  useEffect(() => {
    if (isAuthenticated && userEmail && connectionState === "connected") {
      send({ action: "get_usage", user_email: userEmail });
    }
  }, [isAuthenticated, userEmail, connectionState, send]);

  const handleLogout = useCallback(() => {
    setUserEmail("");
    setIsAuthenticated(false);
    localStorage.removeItem("hw_user_email");
    localStorage.removeItem("hw_user_name");
    localStorage.removeItem("hw_auth_verified");
  }, []);

  const handleForceUploadToggle = useCallback((partNumber: string, forced: boolean) => {
    setForcedParts((prev) => {
      const next = new Set(prev);
      if (forced) next.add(partNumber); else next.delete(partNumber);
      return next;
    });
    // Notify backend about force preference
    if (currentProject) {
      send({
        action: "upload_component_json",
        project_id: currentProject.project_id,
        part_number: partNumber,
        component_json: { _force_use: forced },
      });
    }
  }, [send, currentProject]);

  const handleDownloadComponentJson = useCallback(
    (projectId: string, partNumber: string) => {
      // Request backend to generate component.json for download
      send({ action: "generate_component_json", project_id: projectId, part_number: partNumber });
    },
    [send]
  );

  const handleGenerateAll = useCallback(
    (projectId: string) => {
      // Generate circuit.json for all ICs in component status table
      const ics = componentStatus.filter((c) => c.refdes.startsWith("U") && c.part_number);
      for (const ic of ics) {
        send({ action: "generate_component_json", project_id: projectId, part_number: ic.part_number });
      }
    },
    [send, componentStatus]
  );

  const handleUploadReference = useCallback(
    (projectId: string, file: File, userPrompt: string) => {
      (window as any).__pendingReferenceFile = file;
      (window as any).__pendingReferencePrompt = userPrompt;
      setReferenceStatus("requesting");
      setReferenceProjectId(projectId);
      send({ action: "upload_reference", project_id: projectId, file_name: file.name, request_upload: true });
    },
    [send]
  );

  const handleSymbolApprove = useCallback(
    (projectId: string, partNumber: string, artifactHash: string) => {
      send({ action: "symbol_approve", user_email: userEmail, project_id: projectId, part_number: partNumber, artifact_hash: artifactHash });
    },
    [send, userEmail]
  );

  const handleSymbolReject = useCallback(
    (projectId: string, partNumber: string, reason: string) => {
      send({ action: "symbol_reject", user_email: userEmail, project_id: projectId, part_number: partNumber, reason });
    },
    [send, userEmail]
  );

  const handleSymbolDownload = useCallback(
    (projectId: string, partNumber: string) => {
      send({ action: "symbol_download", project_id: projectId, part_number: partNumber });
    },
    [send]
  );

  const handleEmailSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const input = form.elements.namedItem("email") as HTMLInputElement;
    if (input.value.trim()) {
      setUserEmail(input.value.trim());
      localStorage.setItem("hw_user_email", input.value.trim());
    }
  }, []);

  // ── Connection status label ───────────────────────────────────────
  const statusLabel: Record<typeof connectionState, string> = {
    disconnected: "Disconnected",
    connecting: "Connecting…",
    connected: "Connected",
    reconnecting: "Reconnecting…",
  };

  const statusClass: Record<typeof connectionState, string> = {
    disconnected: "status-disconnected",
    connecting: "status-connecting",
    connected: "status-connected",
    reconnecting: "status-reconnecting",
  };

  return (
    <div className="app">
      <header className="app-header">
        <h1>Agentic EDA</h1>
        <p>
          Describe your circuit in plain English — get manufacturing-ready KiCad
          files.
        </p>
        <span
          className={`connection-status ${statusClass[connectionState]}`}
          role="status"
          aria-live="polite"
        >
          {statusLabel[connectionState]}
          {connectionState === "disconnected" && (
            <button className="reconnect-btn" onClick={connect}>Reconnect</button>
          )}
        </span>
      </header>

      <main>
        {!isAuthenticated ? (
          <AuthForm onAuthenticated={handleAuthenticated} />
        ) : (
          <>
            <div className="mode-toggle">
              <span className="user-badge">
                👤 {userEmail}
                {usageInfo && usageInfo.projects && (
                  <span style={{marginLeft:10,padding:"2px 8px",borderRadius:10,fontSize:11,background:usageInfo.projects.left===0?"#f8514922":"#1f6feb22",color:usageInfo.projects.left===0?"#f85149":"#58a6ff",border:`1px solid ${usageInfo.projects.left===0?"#f85149":"#1f6feb"}`}}>
                    {usageInfo.projects.used}/{usageInfo.projects.limit} projects
                  </span>
                )}
                <button className="logout-btn" onClick={handleLogout}>Sign out</button>
              </span>
              <button
                className={`mt-btn ${appMode === "single" ? "mt-active" : ""}`}
                onClick={() => setAppMode("single")}
              >
                Single Sheet
              </button>
              <button
                className={`mt-btn ${appMode === "project" ? "mt-active" : ""}`}
                onClick={() => setAppMode("project")}
              >
                Multi-Sheet Project
              </button>
              {userEmail.toLowerCase() === "anbunathan.r@gmail.com" && (
                <button
                  className={`mt-btn ${appMode === "admin" ? "mt-active" : ""}`}
                  onClick={() => setAppMode("admin")}
                >
                  Admin
                </button>
              )}
            </div>

            {appMode === "admin" ? (
              <AdminPanel userEmail={userEmail} send={send} />
            ) : appMode === "project" ? (
              <ProjectDashboard
                userEmail={userEmail}
                projects={projects}
                currentProject={currentProject}
                onCreateProject={handleCreateProject}
                onAbortProject={handleAbortProject}
                onListProjects={handleListProjects}
                onSelectProject={handleSelectProject}
                onBuildProject={handleBuildProject}
                onBuildPCB={handleBuildPCB}
                onConfigurePCB={handleConfigurePCB}
                onUploadSchematics={handleUploadSchematics}
                onFileSelected={handleFileSelected}
                onDownloadComponentJson={handleDownloadComponentJson}
                onGenerateAll={handleGenerateAll}
                onForceUploadToggle={handleForceUploadToggle}
                onUploadReference={handleUploadReference}
                isCreating={isCreatingProject}
                isBuilding={isRunning}
                isPCBRunning={isPCBRunning}
                pcbArchiveUrl={pcbArchiveUrl}
                schematicsArchiveUrl={archiveUrl}
                schematicReady={isComplete}
                componentStatus={componentStatus}
                datasheetUploads={datasheetUploads}
                forcedParts={forcedParts}
                referenceStatus={referenceStatus}
                enrichedPreview={enrichedPreview}
                symbolInventory={symbolInventory}
                onSymbolApprove={handleSymbolApprove}
                onSymbolReject={handleSymbolReject}
                onSymbolDownload={handleSymbolDownload}
              />
            ) : (
              <DesignInput onSubmit={handleSubmit} disabled={isRunning} />
            )}

            <ProgressTracker
              currentNode={currentNode}
              messages={progressMessages}
              completed={isComplete}
              isPCBRunning={isPCBRunning}
            />
            <ErrorDisplay errors={errors} />
            <ViolationsPanel
              violations={violations}
              onFix={handleFixViolation}
              onIgnore={handleIgnoreViolation}
              fixingIds={fixingIds}
              fixedIds={fixedIds}
            />
            <DownloadPanel archiveUrl={isPCBRunning || pcbArchiveUrl ? null : archiveUrl} schematicUrl={schematicUrl} />
          </>
        )}
      </main>
    </div>
  );
};

export default App;
